// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { CUSTOM_ELEMENTS_SCHEMA, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SettingsService } from 'src/app/modules/shared/services/settings.service';
import data from '../../../../../assets/i18n/en.json';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import {
  AdvanceSearchRequestModel,
  CountriesResult,
} from 'src/app/api/remote-api/.portal.generated/models';
import {
  EquipmentStatusEnum,
  SupportStatusEnum,
} from '../../constants/advancedSearchConstants';

@Component({
  selector: 'app-advanced-search-pane',
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, NgIf, NgFor, AsyncPipe],
  templateUrl: './advanced-search-pane.component.html',
  styleUrl: './advanced-search-pane.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AdvancedSearchPaneComponent implements OnInit {
  advancedSearchForm: FormGroup = new FormGroup({});
  modalities = this.systemOverviewService.modalities.lastSuccessfulData$;
  equipmentStatus: Array<EquipmentStatusEnum> = [
    EquipmentStatusEnum.All,
    EquipmentStatusEnum.Active,
    EquipmentStatusEnum.Inactive,
  ];

  supportStatus: Array<SupportStatusEnum> = [
    SupportStatusEnum.All,
    SupportStatusEnum.Supported,
    SupportStatusEnum.Limited,
  ];
  activeModalities: Array<string> = [];
  selectedCountries = new Set<CountriesResult>();
  selectedCities = new Set<{ city: string; countryId: string }>();
  selectedProducts = new Set<{
    marketingProductName: string;
    sellingBusinessLine: string;
  }>();
  selectedSoftwares = new Set<{
    swMainVersion: string;
    marketingProductName: string;
    sellingBusinessLine: string;
  }>();
  selectedEquipmentStatus = new Set<string>();
  selectedSupportStatus: boolean | null = null;

  constructor(
    private systemOverviewService: SystemOverviewService,
    private settingsService: SettingsService,
    protected advancedSearchService: AdvancedSearchService,
  ) {
    this.settingsService.modalityIds$.subscribe((response) => {
      if (response.length > 0) {
        this.modalities.subscribe((modalities) => {
          this.activeModalities = modalities
            .filter((modality) => response.includes(modality.id))
            .map((modality) => modality.displayName);
          advancedSearchService.getProductNameList(this.activeModalities);
        });
      }
    });
    this.selectedEquipmentStatus.add(
      EquipmentStatusEnum.Active.toLocaleLowerCase(),
    );
    this.selectedSupportStatus = true;
  }

  ngOnInit(): void {
    this.advancedSearchForm = new FormGroup({
      serialNumber: new FormControl(''),
      materialNumber: new FormControl(''),
      customerName: new FormControl(''),
      functionalLocation: new FormControl(''),
      equipmentId: new FormControl(''),
      equipmentTechId: new FormControl(''),
    });
    this.advancedSearchService.getCountryList();
  }

  onModalityChanged(name: string): void {
    if (this.activeModalities.includes(name)) {
      this.activeModalities = this.activeModalities.filter(
        (modality) => modality !== name,
      );

      const removedProducts = Array.from(this.selectedProducts).filter(
        (product) => product.sellingBusinessLine === name,
      );

      this.selectedProducts = new Set(
        Array.from(this.selectedProducts).filter(
          (product) => product.sellingBusinessLine !== name,
        ),
      );

      const removedProductNames = new Set(
        removedProducts.map((p) => p.marketingProductName),
      );

      this.selectedSoftwares = new Set(
        Array.from(this.selectedSoftwares).filter(
          (software) => !removedProductNames.has(software.marketingProductName),
        ),
      );
    } else {
      this.activeModalities.push(name);
    }

    if (this.activeModalities.length !== 0) {
      this.advancedSearchService.getProductNameList(this.activeModalities);
    }
  }

  selectCountry(countryModal: CountriesResult): void {
    this.selectedCountries.add(countryModal);
    this.updateHtmlElements(countryModal, data['label.country']);
    this.advancedSearchService.getCityList(
      Array.from(this.selectedCountries).map(
        (country) => country.countryId ?? '',
      ),
    );
  }

  unselectCountry(countryModal: CountriesResult): void {
    this.selectedCountries.delete(countryModal);
    this.selectedCities = new Set(
      Array.from(this.selectedCities).filter(
        (city) => city.countryId !== countryModal.countryId,
      ),
    );
    if (this.selectedCountries.size !== 0) {
      this.advancedSearchService.getCityList(
        Array.from(this.selectedCountries).map(
          (country) => country.countryId ?? '',
        ),
      );
    }
  }

  selectCity(city: { city: string; countryId: string }): void {
    this.selectedCities.add(city);
    this.updateHtmlElements(city.city, data['label.city']);
  }

  isCitySelected(city: { city: string; countryId: string }): boolean {
    return Array.from(this.selectedCities).some(
      (c) => c.city === city.city && c.countryId === city.countryId,
    );
  }

  unselectCity(city: { city: string; countryId: string }): void {
    this.selectedCities.delete(city);
  }

  selectProduct(product: {
    marketingProductName: string;
    sellingBusinessLine: string;
  }): void {
    this.selectedProducts.add(product);
    this.updateHtmlElements(
      product.marketingProductName,
      data['label.productName'],
    );
    this.advancedSearchService.getSoftwareNameList(
      this.activeModalities,
      Array.from(this.selectedProducts).map((p) => p.marketingProductName),
    );
  }

  unselectProduct(product: {
    marketingProductName: string;
    sellingBusinessLine: string;
  }): void {
    this.selectedProducts.delete(product);
    this.selectedSoftwares = new Set(
      Array.from(this.selectedSoftwares).filter(
        (software) =>
          software.marketingProductName !== product.marketingProductName,
      ),
    );
    if (this.selectedProducts.size !== 0) {
      this.advancedSearchService.getSoftwareNameList(
        this.activeModalities,
        Array.from(this.selectedProducts).map((p) => p.marketingProductName),
      );
    }
  }

  isProductSelected(product: {
    marketingProductName: string;
    sellingBusinessLine: string;
  }): boolean {
    return Array.from(this.selectedProducts).some(
      (p) =>
        p.marketingProductName === product.marketingProductName &&
        p.sellingBusinessLine === product.sellingBusinessLine,
    );
  }

  selectSoftware(software: {
    swMainVersion: string;
    marketingProductName: string;
    sellingBusinessLine: string;
  }): void {
    this.selectedSoftwares.add(software);
    this.updateHtmlElements(
      software.swMainVersion,
      data['label.softwareVersion'],
    );
  }

  unselectSoftware(software: {
    swMainVersion: string;
    marketingProductName: string;
    sellingBusinessLine: string;
  }): void {
    this.selectedSoftwares.delete(software);
  }

  isSoftwareSelected(software: {
    swMainVersion: string;
    marketingProductName: string;
    sellingBusinessLine: string;
  }): boolean {
    return Array.from(this.selectedSoftwares).some(
      (s) =>
        s.swMainVersion === software.swMainVersion &&
        s.marketingProductName === software.marketingProductName &&
        s.sellingBusinessLine === software.sellingBusinessLine,
    );
  }

  selectEquipmentStatus(equipmentStatus: string): void {
    this.selectedEquipmentStatus.clear();
    if (equipmentStatus !== EquipmentStatusEnum.All) {
      this.selectedEquipmentStatus.add(equipmentStatus.toLocaleLowerCase());
      return;
    }
    this.selectedEquipmentStatus.add(
      EquipmentStatusEnum.Active.toLocaleLowerCase(),
    );
    this.selectedEquipmentStatus.add(
      EquipmentStatusEnum.Inactive.toLocaleLowerCase(),
    );
  }

  selectSupportStatus(supportStatus: string): void {
    switch (supportStatus) {
      case SupportStatusEnum.All:
        this.selectedSupportStatus = null;
        break;
      case SupportStatusEnum.Supported:
        this.selectedSupportStatus = true;
        break;
      case SupportStatusEnum.Limited:
        this.selectedSupportStatus = false;
        break;
    }
  }

  updateHtmlElements(
    item: string | CountriesResult,
    type: string,
  ): void {
    if (typeof item === 'string') {
      document.querySelector(`[label="${item}"]`)?.setAttribute('active', '');
    } else {
      document
        .querySelector(`[label="${item.countryName} (${item.countryId})"]`)
        ?.setAttribute('active', '');
    }
    document.querySelector(`[label="${type}"]`)?.removeAttribute('active');
    document.querySelector(`[label="${type}"]`)?.removeAttribute('value');
  }

  clearInputValue(formName: string): void {
    this.advancedSearchForm.get(formName)?.setValue('');
  }

  resetFilter(): void {
    this.selectedCountries.clear();
    this.selectedCities.clear();
    this.selectedProducts.clear();
    this.selectedSoftwares.clear();
    this.advancedSearchForm.reset({
      serialNumber: '',
      materialNumber: '',
      customerName: '',
      functionalLocation: '',
      equipmentId: '',
      equipmentTechId: '',
    });
    this.settingsService.modalityIds$.subscribe((response) => {
      if (response.length > 0) {
        this.modalities.subscribe((modalities) => {
          this.activeModalities = modalities
            .filter((modality) => response.includes(modality.id))
            .map((modality) => modality.displayName);
          this.advancedSearchService.getProductNameList(this.activeModalities);
        });
      }
    });
    document
      .getElementById('equipment-status-dropdown')
      ?.setAttribute('value', 'Active');
    document
      .getElementById('support-status-dropdown')
      ?.setAttribute('value', 'Supported');
    this.selectedEquipmentStatus.clear();
    this.selectedEquipmentStatus.add(
      EquipmentStatusEnum.Active.toLocaleLowerCase(),
    );
    this.selectedSupportStatus = true;
  }

  private createFilter(): AdvanceSearchRequestModel {
    let serialNumberValue: Array<string> = [];
    let materialNumberValue: Array<number> = [];
    let customerNameValue: Array<string> = [];
    let functionalLocationValue: Array<string> = [];
    let equipmentIdValue: Array<string> = [];
    let equipmentTechIdValue: Array<string> = [];

    if (this.advancedSearchForm.get('serialNumber')?.value !== '') {
      serialNumberValue = [this.advancedSearchForm.get('serialNumber')?.value];
    }

    if (this.advancedSearchForm.get('materialNumber')?.value !== '') {
      materialNumberValue = [
        Number(this.advancedSearchForm.get('materialNumber')?.value),
      ];
    }

    if (this.advancedSearchForm.get('customerName')?.value !== '') {
      customerNameValue = [this.advancedSearchForm.get('customerName')?.value];
    }

    if (this.advancedSearchForm.get('functionalLocation')?.value !== '') {
      functionalLocationValue = [
        this.advancedSearchForm.get('functionalLocation')?.value,
      ];
    }

    if (this.advancedSearchForm.get('equipmentId')?.value !== '') {
      equipmentIdValue = [this.advancedSearchForm.get('equipmentId')?.value];
    }

    if (this.advancedSearchForm.get('equipmentTechId')?.value !== '') {
      equipmentTechIdValue = [
        this.advancedSearchForm.get('equipmentTechId')?.value,
      ];
    }

    return {
      serialNumbers: serialNumberValue,
      materialNumbers: materialNumberValue,
      customerNames: customerNameValue,
      functionalLocations: functionalLocationValue,
      equipmentIds: equipmentIdValue,
      equipmentTechIds: equipmentTechIdValue,
      countries: Array.from(this.selectedCountries).map(
        (country) => country.countryId ?? '',
      ),
      cities: Array.from(this.selectedCities).map((c) => c.city),
      modalityNames: this.activeModalities,
      productNames: Array.from(this.selectedProducts).map(
        (p) => p.marketingProductName,
      ),
      softwareVersions: Array.from(this.selectedSoftwares).map(
        (s) => s.swMainVersion,
      ),
      equipmentActive: Array.from(this.selectedEquipmentStatus),
      isSupported: this.selectedSupportStatus,
    };
  }

  onSubmit(): void {
    const filter: AdvanceSearchRequestModel = this.createFilter();
    this.advancedSearchService.getSearchResults(filter);
  }
}
